import type { AcademicAnalyticsResult, GraduationCandidate } from '@dosfilos/application';
import { GAP_BUCKETS, type GapBucket } from './analytics-charts.model';

// ─── Panel académico: las listas CON NOMBRES (solo gestión) ─────────────────
// El servidor ya entrega los ids (tramos de la última inscripción, repitencia)
// y Candidatos ya trae los cursos que faltan; los nombres de las dos primeras se
// piden aparte (`getAnalyticsPeople`) y SOLO al abrir una lista. Orden
// alfabético por apellido: nunca un ranking ni un orden por nota.

export interface PersonName {
  id: string;
  firstName: string;
  lastName: string;
  studentCode: string | null;
}

/** El tope de ids por pedido de `getAnalyticsPeople` (ANALYTICS_PEOPLE_MAX). */
export const PEOPLE_PER_REQUEST = 300;

/** Parte los ids en pedidos que respetan el tope del servidor (sin repetir). */
export function peopleRequests(ids: readonly string[], max = PEOPLE_PER_REQUEST): string[][] {
  const unique = [...new Set(ids)];
  const out: string[][] = [];
  for (let i = 0; i < unique.length; i += max) out.push(unique.slice(i, i + max));
  return out;
}

const byName = (
  a: { lastName: string; firstName: string },
  b: { lastName: string; firstName: string },
) =>
  a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' }) ||
  a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' });

export const displayName = (p: { firstName: string; lastName: string }) =>
  [p.lastName, p.firstName].filter(Boolean).join(', ');

// ── a) Cerca de egresar y sin inscripción reciente (una celda de la tabla d) ──
export interface NearRow {
  personId: string;
  firstName: string;
  lastName: string;
  studentCode: string | null;
  coursesMissing: number;
  requiredMissing: string[];
  groupShortfall: number;
  lastTerm: string | null;
}

/** Los candidatos de una celda (cursos que faltan × tramo), uno por persona con
 *  su credencial más cercana: los mismos datos que la pestaña Candidatos. */
export function nearList(
  candidates: readonly GraduationCandidate[],
  gapBuckets: AcademicAnalyticsResult['lastEnrollment']['buckets'],
  cell: { missing: number; gap: GapBucket } | null,
  near: number,
): NearRow[] {
  const gapOf = new Map<string, GapBucket>();
  for (const b of GAP_BUCKETS) for (const id of gapBuckets[b] ?? []) gapOf.set(id, b);
  const best = new Map<string, GraduationCandidate>();
  for (const c of candidates) {
    const m = c.eligible ? 0 : c.coursesMissing;
    if (m > near) continue;
    const prev = best.get(c.personId);
    const pm = prev ? (prev.eligible ? 0 : prev.coursesMissing) : Infinity;
    if (m < pm) best.set(c.personId, c);
  }
  return [...best.values()]
    .filter((c) => {
      if (!cell) return true;
      const m = c.eligible ? 0 : c.coursesMissing;
      return m === cell.missing && gapOf.get(c.personId) === cell.gap;
    })
    .map((c) => ({
      personId: c.personId,
      firstName: c.firstName,
      lastName: c.lastName,
      studentCode: c.studentCode,
      coursesMissing: c.eligible ? 0 : c.coursesMissing,
      requiredMissing: c.requiredMissing,
      groupShortfall: c.groupShortfall,
      lastTerm: c.lastTerm,
    }))
    .sort(byName);
}

// ── b) Tiempo desde la última inscripción (un tramo) ────────────────────────
export interface GapRow {
  personId: string;
  firstName: string;
  lastName: string;
  studentCode: string | null;
  lastTerm: string | null;
}

export function gapList(
  r: AcademicAnalyticsResult,
  bucket: GapBucket,
  people: ReadonlyMap<string, PersonName>,
): GapRow[] {
  return (r.lastEnrollment.buckets[bucket] ?? [])
    .map((id) => {
      const p = people.get(id);
      return {
        personId: id,
        firstName: p?.firstName ?? '',
        lastName: p?.lastName ?? '',
        studentCode: p?.studentCode ?? null,
        lastTerm: r.lastEnrollment.lastTerm?.[id] ?? null,
      };
    })
    .sort(byName);
}

// ── c) Repitencia (debajo del gráfico de notas) ─────────────────────────────
export interface RepeatRow {
  personId: string;
  firstName: string;
  lastName: string;
  studentCode: string | null;
  courseId: string;
  courseCode: string;
  attempts: number;
}

/** Alumnos con 2 o más intentos con nota final en un curso (la W no cuenta),
 *  en los años elegidos; `courseId` filtra por curso. */
export function repeatList(
  r: AcademicAnalyticsResult,
  people: ReadonlyMap<string, PersonName>,
  courses: ReadonlyMap<string, { code: string }>,
  courseId: string | null,
): RepeatRow[] {
  return r.repeaters
    .filter((x) => !courseId || x.courseId === courseId)
    .map((x) => {
      const p = people.get(x.studentId);
      return {
        personId: x.studentId,
        firstName: p?.firstName ?? '',
        lastName: p?.lastName ?? '',
        studentCode: p?.studentCode ?? null,
        courseId: x.courseId,
        courseCode: courses.get(x.courseId)?.code ?? x.courseId,
        attempts: x.attempts,
      };
    })
    .sort((a, b) => byName(a, b) || a.courseCode.localeCompare(b.courseCode));
}

// ── CSV ─────────────────────────────────────────────────────────────────────
const cell = (v: unknown): string => {
  const s = String(v ?? '');
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** CSV con BOM (Excel lee bien los acentos). Identifica por código de alumno,
 *  nunca por un id interno. */
export function listCsv(
  header: readonly string[],
  rows: ReadonlyArray<readonly unknown[]>,
): string {
  return '﻿' + [header, ...rows].map((row) => row.map(cell).join(',')).join('\r\n') + '\r\n';
}

// ── Anexo del informe carta (casilla «Incluir listas con nombres») ──────────
export interface NamedAnnex {
  near: NearRow[];
  gaps: Array<{ bucket: GapBucket; rows: GapRow[] }>;
  repeat: RepeatRow[];
}

/** Las tres listas completas, con los mismos filtros que la pantalla. Solo se
 *  arma cuando quien exporta marcó la casilla (apagada por defecto). */
export function buildAnnex(
  r: AcademicAnalyticsResult,
  candidates: readonly GraduationCandidate[],
  near: number,
  people: ReadonlyMap<string, PersonName>,
  courses: ReadonlyMap<string, { code: string }>,
): NamedAnnex {
  return {
    near: nearList(candidates, r.lastEnrollment.buckets, null, near),
    gaps: GAP_BUCKETS.map((bucket) => ({ bucket, rows: gapList(r, bucket, people) })),
    repeat: repeatList(r, people, courses, null),
  };
}

/** Los ids cuyos nombres pide el anexo (tramos y repitencia). */
export function annexPeopleIds(r: AcademicAnalyticsResult): string[] {
  return [
    ...new Set([
      ...GAP_BUCKETS.flatMap((b) => r.lastEnrollment.buckets[b] ?? []),
      ...r.repeaters.map((x) => x.studentId),
    ]),
  ];
}
